import React, { useEffect, useState } from "react";
import { 
  Phone, Users, Calendar, AlertTriangle, TrendingUp, TrendingDown,
  Clock, CheckCircle, Activity, Zap, ArrowUpRight, BarChart3
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_API_KEY = "test_key_123";

export const OverviewPage = () => {
  const [stats, setStats] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const headers = { "X-API-Key": DEMO_API_KEY };
        
        const [statsRes, billingRes] = await Promise.all([
          fetch(`${API_URL}/api/stats/dashboard`, { headers }).catch(() => null),
          fetch(`${API_URL}/api/billing/overview`, { headers }).catch(() => null)
        ]);

        if (statsRes?.ok) {
          setStats(await statsRes.json());
        }
        if (billingRes?.ok) {
          setBilling(await billingRes.json());
        }
      } catch (err) {
        console.error("Failed to load dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Calculate values from billing data
  const minutesUsed = Math.round(billing?.usage?.minutes_used || 0);
  const minutesIncluded = billing?.usage?.minutes_included || 300;
  const usagePercent = billing?.usage?.usage_percent || 0;
  const callsCount = billing?.usage?.calls_count || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with greeting */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            Welcome back! 👋
          </h1>
          <p className="text-slate-500 mt-1">
            Here's what's happening with your AI receptionist today.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
          <span className="text-emerald-700 text-sm font-medium">AI Agent Active</span>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Calls"
          value={callsCount}
          change="+12%"
          changeType="positive"
          icon={Phone}
          color="blue"
          subtitle="This month"
        />
        <StatCard
          title="Minutes Used"
          value={minutesUsed}
          suffix={`/${minutesIncluded}`}
          icon={Clock}
          color="purple"
          subtitle={`${usagePercent.toFixed(0)}% of plan`}
          progress={usagePercent}
        />
        <StatCard
          title="Leads Captured"
          value={stats?.leads_last_24h || 8}
          change="+23%"
          changeType="positive"
          icon={Users}
          color="emerald"
          subtitle="This month"
        />
        <StatCard
          title="Bookings Made"
          value={stats?.bookings_last_7d || 5}
          change="+8%"
          changeType="positive"
          icon={Calendar}
          color="orange"
          subtitle="This week"
        />
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart Placeholder */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-slate-900">Call Activity</h3>
              <p className="text-sm text-slate-500">Last 7 days performance</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                Calls
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                Bookings
              </span>
            </div>
          </div>
          
          {/* Simple Bar Chart */}
          <div className="flex items-end justify-between h-40 gap-2">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => {
              const calls = [12, 18, 15, 22, 28, 8, 5][i];
              const bookings = [3, 5, 4, 6, 8, 2, 1][i];
              const maxHeight = 28;
              return (
                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex gap-1 justify-center items-end h-32">
                    <div 
                      className="w-4 bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                      style={{ height: `${(calls / maxHeight) * 100}%` }}
                    ></div>
                    <div 
                      className="w-4 bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                      style={{ height: `${(bookings / maxHeight) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-slate-500">{day}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-2 mb-6">
            <Zap className="w-5 h-5 text-yellow-400" />
            <h3 className="font-semibold">Performance</h3>
          </div>
          
          <div className="space-y-5">
            <PerformanceItem 
              label="Answer Rate" 
              value="98%" 
              color="emerald"
            />
            <PerformanceItem 
              label="Avg Call Duration" 
              value="2m 34s" 
              color="blue"
            />
            <PerformanceItem 
              label="Booking Conversion" 
              value="32%" 
              color="purple"
            />
            <PerformanceItem 
              label="Customer Satisfaction" 
              value="4.8/5" 
              color="yellow"
            />
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Recent Calls</h3>
            <a href="/dashboard/calls" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              View all <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
          
          <div className="space-y-3">
            {[
              { name: "John Smith", time: "2 min ago", type: "Booking", status: "success" },
              { name: "Sarah Johnson", time: "15 min ago", type: "Inquiry", status: "info" },
              { name: "Mike Wilson", time: "1 hour ago", type: "Lead", status: "warning" },
              { name: "Emily Davis", time: "2 hours ago", type: "Booking", status: "success" },
            ].map((call, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    call.status === "success" ? "bg-emerald-100 text-emerald-600" :
                    call.status === "warning" ? "bg-amber-100 text-amber-600" :
                    "bg-blue-100 text-blue-600"
                  }`}>
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{call.name}</p>
                    <p className="text-xs text-slate-500">{call.time}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  call.status === "success" ? "bg-emerald-100 text-emerald-700" :
                  call.status === "warning" ? "bg-amber-100 text-amber-700" :
                  "bg-blue-100 text-blue-700"
                }`}>
                  {call.type}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">System Status</h3>
            <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
              All Systems Operational
            </span>
          </div>
          
          <div className="space-y-3">
            <SystemStatus label="AI Voice Agent" status="operational" latency="45ms" />
            <SystemStatus label="Call Routing" status="operational" latency="12ms" />
            <SystemStatus label="Booking Engine" status="operational" latency="89ms" />
            <SystemStatus label="SMS Notifications" status="operational" latency="156ms" />
            <SystemStatus label="Recording Storage" status="operational" latency="234ms" />
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Uptime (30 days)</span>
              <span className="font-semibold text-emerald-600">99.98%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function StatCard({ title, value, suffix, change, changeType, icon: Icon, color, subtitle, progress }) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    orange: "bg-orange-50 text-orange-600 border-orange-100",
  };

  const iconBg = {
    blue: "bg-blue-100",
    purple: "bg-purple-100",
    emerald: "bg-emerald-100",
    orange: "bg-orange-100",
  };

  return (
    <div className={`rounded-2xl border p-5 ${colorClasses[color]} transition-all hover:shadow-lg hover:-translate-y-1`}>
      <div className="flex items-start justify-between">
        <div className={`w-12 h-12 rounded-xl ${iconBg[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
        {change && (
          <span className={`flex items-center gap-1 text-xs font-medium ${
            changeType === "positive" ? "text-emerald-600" : "text-red-600"
          }`}>
            {changeType === "positive" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {change}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium opacity-80">{title}</p>
        <p className="text-3xl font-bold mt-1">
          {value}
          {suffix && <span className="text-lg font-normal opacity-60">{suffix}</span>}
        </p>
        <p className="text-xs mt-1 opacity-70">{subtitle}</p>
      </div>
      {progress !== undefined && (
        <div className="mt-3 h-2 bg-white/50 rounded-full overflow-hidden">
          <div 
            className="h-full bg-current rounded-full transition-all"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function PerformanceItem({ label, value, color }) {
  const colors = {
    emerald: "text-emerald-400",
    blue: "text-blue-400",
    purple: "text-purple-400",
    yellow: "text-yellow-400",
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span className={`font-bold ${colors[color]}`}>{value}</span>
    </div>
  );
}

function SystemStatus({ label, status, latency }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
      <div className="flex items-center gap-3">
        <span className={`w-2 h-2 rounded-full ${
          status === "operational" ? "bg-emerald-500" : "bg-red-500"
        }`}></span>
        <span className="text-slate-700 font-medium">{label}</span>
      </div>
      <span className="text-xs text-slate-500">{latency}</span>
    </div>
  );
}

export default OverviewPage;
